/* =============================================================================
   VIEWS / ADMIN / LAYOUT-EDITOR — Tischplan mit Drag & Drop
   =============================================================================
   - renderLayout + renderLayoutAreas
   - startDragTable / dragMove / dragEnd (Drag & Drop)
   - addTable / editTable / saveTableEdit / deleteTable
   - resetTablesToDefault
============================================================================ */

import { htmlEscape, uid, showToast, requireEditPermission } from '../../utils/helpers.js';
import { closeModal } from '../../utils/dom.js';
import { tableExtraChairs } from '../../core/capacity.js';
// S243 Cut A — QR-Aufsteller für die Tische.
import { generateQrSvg } from '../../utils/qr-code.js';
import { tableQrUrl, resolveGuestOrigin } from '../../core/table-qr.js';
import { getBrandLogoSrc } from '../../core/branding.js';
// S204 — Freiform-Geometrie + statischer Element-Render leben jetzt neutral in
// core/layout-render.js (der Gast-Wunschtisch-Plan nutzt sie ohne admin-Import).
// Hier für den internen Editor-Gebrauch importiert UND per `export *` re-exportiert,
// damit der bestehende Import-Vertrag (occupancy.js/layout-editor.test.js/main.js)
// unverändert bleibt (Memory gg-module-extract: explizit ex-/importiert).
import {
  ELEMENT_KINDS, KIND_META, GRID, CANVAS_DEFAULT,
  snap10, snapPoint, snapRotation5, clampRotation, rectPolygon, polyBBox,
  _elementBox, elementsForArea, canvasSizeForArea
} from '../../core/layout-render.js';
export * from '../../core/layout-render.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { tables: [], settings: {}, layoutElements: [], layoutAreas: [] };
}

/** Vertex i verschieben (gesnappt) — reine Kopie. */
export function moveVertex(points, i, np) {
  const out = (points || []).map(p => ({ x: p.x, y: p.y }));
  if (i >= 0 && i < out.length) out[i] = snapPoint(np);
  return out;
}

/** S205-HF — GANZE Kante i (Punkte i & i+1, umlaufend) um loc={x,y} verschieben,
 *  jeder Endpunkt gesnappt — reine Kopie. Ermöglicht das Ziehen einer kompletten
 *  Kante statt zweier Punkte nacheinander. */
export function moveEdge(points, i, loc) {
  const out = (points || []).map(p => ({ x: p.x, y: p.y }));
  const n = out.length;
  if (n < 2 || i < 0 || i >= n) return out;
  const dx = (loc && Number(loc.x)) || 0, dy = (loc && Number(loc.y)) || 0;
  const j = (i + 1) % n;
  out[i] = snapPoint({ x: out[i].x + dx, y: out[i].y + dy });
  out[j] = snapPoint({ x: out[j].x + dx, y: out[j].y + dy });
  return out;
}

/** Vertex als Kanten-Mittelpunkt einfügen (Kante edgeIndex→edgeIndex+1, umlaufend). */
export function addVertex(points, edgeIndex) {
  const out = (points || []).map(p => ({ x: p.x, y: p.y }));
  const n = out.length;
  if (n < 2) return out;
  const a = out[edgeIndex], b = out[(edgeIndex + 1) % n];
  if (!a || !b) return out;
  out.splice(edgeIndex + 1, 0, snapPoint({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }));
  return out;
}

/** Vertex löschen — Guard: nicht unter Minimum (Linie ≥2, Fläche ≥3). */
export function removeVertex(points, i, minPts) {
  const min = minPts || 2;
  const out = (points || []).map(p => ({ x: p.x, y: p.y }));
  if (out.length <= min) return out;
  if (i >= 0 && i < out.length) out.splice(i, 1);
  return out;
}

/** Default-Element frisch platziert (mittig, gesnappt). Poly-Arten bekommen ein
 *  Rechteck-Polygon zum Weiterformen; Marker points=null + Default-Größe. */
export function defaultElement(kind, area, canvasW, canvasH, zIndex) {
  const m = KIND_META[kind];
  if (!m) return null;
  const cw = Number(canvasW) || CANVAS_DEFAULT.w, ch = Number(canvasH) || CANVAS_DEFAULT.h;
  const el = {
    area, kind, rotation: 0,
    closed: !!(m.poly && !m.line),          // Fläche (counter) geschlossen; Linie offen
    thickness: m.thickness || 10,
    w: m.w, h: m.h,
    label: kind === 'label' ? 'Text' : null,
    zIndex: Number.isFinite(zIndex) ? zIndex : 0,
    // Jonas-Fix (S204): Linien-Elemente (Wand/Fenster) starten als EINFACHE
    // 2-Punkt-Linie (Anfang→Ende), die per Mittelpunkt-Add erweiterbar ist — NICHT
    // als 4-Punkt-Rechteck-Umriss (wirkte „doppelwandig"). Flächen (Theke) bleiben
    // Rechteck-Polygon; Marker haben keine Punkte.
    points: m.poly ? (m.line ? [{ x: 0, y: 0 }, { x: snap10(m.w) || GRID, y: 0 }] : rectPolygon(m.w, m.h)) : null,
    x: snap10(Math.max(0, (cw - m.w) / 2)),
    y: snap10(Math.max(0, (ch - m.h) / 2))
  };
  return el;
}

/** Außenwände = EIN geschlossener Wand-Linienzug (Rechteck-Umriss auf den Canvas-
 *  Kanten) — danach frei per Vertex reshapebar (Schrägen/Diagonalen). */
export function outerWallsElement(area, canvasW, canvasH, zIndex) {
  const cw = snap10(canvasW || CANVAS_DEFAULT.w), ch = snap10(canvasH || CANVAS_DEFAULT.h);
  const T = KIND_META.wall.thickness || 10;
  const inset = snap10(T / 2);
  return {
    area, kind: 'wall', rotation: 0, closed: true,
    thickness: T, w: cw, h: ch, label: null,
    zIndex: Number.isFinite(zIndex) ? zIndex : 0,
    x: 0, y: 0,
    points: [
      { x: inset,      y: inset },
      { x: cw - inset, y: inset },
      { x: cw - inset, y: ch - inset },
      { x: inset,      y: ch - inset }
    ]
  };
}


// Editor-interne Bequemlichkeits-Wrapper (lesen den Live-Cache).
function canvasSizeFor(area) { return canvasSizeForArea(getState(), area); }
function elementsFor(area) { return elementsForArea(getState(), area); }

// -----------------------------------------------------------------------------
// Pauspapier-Raster: Toggle-Preference (localStorage, Default AN). Reine Editor-
// Hilfe — rendert NUR im Editor (renderLayoutAreas), nie in Belegung/Live.
// -----------------------------------------------------------------------------
const LEL_GRID_KEY = 'gg_lel_grid';
function gridEnabled() {
  try { return localStorage.getItem(LEL_GRID_KEY) !== '0'; } catch (_) { return true; }
}
function setGridEnabled(on) {
  try { localStorage.setItem(LEL_GRID_KEY, on ? '1' : '0'); } catch (_) { /* ignore */ }
}

export function renderLayout() {
  _ensureEscDeselect();   // S205-HOTFIX — Escape deselektiert (einmalig gebunden)
  const gridOn = gridEnabled();
  const html = `
    <div class="layout-toolbar">
      <strong>Tisch hinzufügen:</strong>
      <select id="new-table-area">
        <option value="indoor">Innen</option>
        <option value="outdoor">Außen</option>
        <option value="club">Club</option>
      </select>
      <select id="new-table-seats">
        <option value="2">2 Personen</option>
        <option value="4">4 Personen</option>
        <option value="6">6 Personen</option>
        <option value="10">10 Personen</option>
        <option value="12">12 Personen</option>
      </select>
      <label class="checkbox" style="font-size:.85rem;">
        <input type="checkbox" id="new-table-extendable" checked>
        kombinierbar
      </label>
      <label style="font-size:.85rem; display:inline-flex; align-items:center; gap:6px;">
        Zusatzstühle:
        <input type="number" id="new-table-extra-chairs" min="0" max="6" step="1" value="0" style="width:60px;">
      </label>
      <button class="btn btn-primary btn-sm" onclick="addTable()">+ Tisch hinzufügen</button>
      <!-- S243 Cut A — QR-Aufsteller für die „Handy-Bestellung am Tisch". -->
      <button class="btn btn-ghost btn-sm" style="margin-left:auto;" onclick="openTableQrPrint()" title="Druckvorlage mit einem QR-Aufsteller je Tisch">🖨️ QR-Codes drucken</button>
      <button class="btn btn-ghost btn-sm" onclick="resetTablesToDefault()">Standard wiederherstellen</button>
    </div>

    <!-- S199 Raumgestalter — Element-Palette + Pauspapier-Steuerung -->
    <div class="lel-toolbar">
      <strong>Element in&nbsp;</strong>
      <select id="lel-area" onchange="onLelAreaChange()">
        <option value="indoor">Innen</option>
        <option value="outdoor">Außen</option>
        <option value="club">Club</option>
      </select>
      <div class="lel-palette">
        ${ELEMENT_KINDS.map(k => `<button class="lel-chip lel-chip-${k}" onclick="addElement('${k}')" title="${htmlEscape(KIND_META[k].label)} einfügen">${KIND_META[k].emoji} ${htmlEscape(KIND_META[k].label)}</button>`).join('')}
      </div>
      <button class="btn btn-ghost btn-sm" onclick="insertOuterWalls()" title="Rechteck-Wandrahmen auf den Canvas-Kanten — danach frei per Ecken reshapebar">⬜ Außenwände</button>
      <button class="btn btn-ghost btn-sm ${gridOn ? 'active' : ''}" id="lel-grid-btn" aria-pressed="${gridOn ? 'true' : 'false'}" onclick="toggleLelGrid()" title="Pauspapier-Raster ein/aus (nur im Editor)">📐 Raster</button>
      <span class="lel-canvas-size">
        Canvas <input type="number" id="lel-cw" min="200" max="4000" step="10" style="width:66px;">
        × <input type="number" id="lel-ch" min="200" max="4000" step="10" style="width:66px;">
        <button class="btn btn-ghost btn-sm" onclick="applyCanvasSize()">↔ Größe</button>
      </span>
    </div>
    <div class="lel-legend">
      ${ELEMENT_KINDS.map(k => `<span class="lel-legend-item"><span class="lel-swatch lel-sw-${k}"></span>${htmlEscape(KIND_META[k].label)}</span>`).join('')}
    </div>

    <div class="layout-areas" id="layout-areas"></div>
    <div class="card" style="margin-top: 16px;">
      <p style="margin:0; color:var(--ink-soft); font-size:.9rem;">
        💡 <strong>Tische:</strong> per Drag &amp; Drop verschieben (frei), Doppelklick öffnet die Bearbeitung.
        Gestrichelter Rahmen = <strong>feste 2er</strong> (nicht kombinierbar).<br>
        🏗️ <strong>Struktur-Elemente:</strong> Palette klicken → Element erscheint mittig. Ziehen (rastet aufs 10-px-Gitter),
        Doppelklick = Drehen (45°)/Text/Löschen. Bei Wand/Fenster/Theke im Bearbeiten-Dialog „🔺 Ecken bearbeiten" →
        Punkte ziehen/„+"-einfügen/Doppelklick-löschen für Schrägen &amp; Diagonalen.
      </p>
    </div>
    <!-- S58 Hotfix-2 — Restore-Sektion für soft-gelöschte Tische (Konsistenz zu Menu-Editor) -->
    <details class="card" style="margin-top:14px;">
      <summary style="cursor:pointer; font-weight:600; padding:4px 0;">
        🗑️ Gelöschte Tische (wiederherstellbar)
      </summary>
      <div id="tables-deleted-list" style="margin-top:10px;"></div>
    </details>
  `;
  const tab = document.getElementById('tab-layout');
  if (tab) tab.innerHTML = html;
  onLelAreaChange();          // Canvas-Größe-Inputs auf den aktiven Bereich setzen
  renderLayoutAreas();
  // S58 Hotfix-2: Restore-View asynchron nachladen — analog menu-editor.js.
  renderDeletedTablesList().catch(e => console.warn('[layout] renderDeletedTablesList:', e));
}

export function renderLayoutAreas() {
  const state = getState();
  const wrap = document.getElementById('layout-areas');
  if (!wrap) return;
  const areas = [
    { id: 'indoor',  label: 'Innenbereich' },
    { id: 'club',    label: 'Clubraum' },
    { id: 'outdoor', label: 'Außenbereich' }
  ];
  const gridCls = gridEnabled() ? ' lel-grid' : '';
  wrap.innerHTML = areas.map(area => {
    const tables = (state.tables || []).filter(t => t.area === area.id);
    const enabled = area.id === 'outdoor' ? state.settings.outdoorEnabled
                  : area.id === 'club'    ? state.settings.clubEnabled
                  : true;
    const cap = tables.reduce((s, t) => s + t.seats, 0);
    // S207-HOTFIX (Jonas) — Zusatzstühle in der Platz-Anzeige sichtbar machen: die
    // Kopfzeile zeigt jetzt neben den festen Sitzplätzen die maximale Kapazität inkl.
    // hinterlegter Zusatzstühle. (Nur Anzeige — das Spontan-Reserve-Gate/totalCapacity
    // rechnet bewusst weiter mit festen Sitzen, damit kein Überbuchungs-Puffer wegfällt.)
    const chairTotal = tables.reduce((s, t) => s + (Number(tableExtraChairs(t)) || 0), 0);
    const size = canvasSizeFor(area.id);
    const els = elementsFor(area.id);   // aktiv, nach z_index (Ebene UNTER den Tischen)
    return `
      <div class="layout-area">
        <div class="layout-area-header">
          <h3>${area.label} ${!enabled ? '<span class="badge badge-declined" style="margin-left:8px;">deaktiviert</span>' : ''}</h3>
          <div style="font-size:.85rem; color:var(--ink-soft);">${tables.length} Tische · ${cap} Plätze${chairTotal ? ` (bis ${cap + chairTotal} mit 🪑)` : ''}${els.length ? ` · ${els.length} Elemente` : ''}</div>
        </div>
        <div class="layout-canvas${gridCls} ${enabled ? '' : 'disabled'}" data-area="${area.id}" id="canvas-${area.id}"
             onmousedown="lelCanvasMouseDown(event)"
             style="width:${size.w}px; height:${size.h}px;">
          ${els.map(renderElementNode).join('')}
          ${tables.map(t => {
            const ec = tableExtraChairs(t);
            const isDisabled = t.disabled === true;
            const titleParts = [];
            if (isDisabled) titleParts.push('Außer Betrieb — wird nicht verwendet');
            else if (t.extendable === false) titleParts.push('Fester Tisch (nicht kombinierbar)');
            else titleParts.push('Kombinierbar — kann mit Nachbartischen zusammengeschoben werden');
            if (ec > 0) titleParts.push(`+${ec} Zusatzstuhl${ec > 1 ? 'e' : ''} möglich`);
            return `
            <div class="table-node seats-${Math.min(12, t.seats)} shape-${t.shape || 'rectangular'} ${isDisabled ? 'disabled-table' : ''}"
                 data-id="${t.id}"
                 style="left:${t.x}px; top:${t.y}px;"
                 onmousedown="startDragTable(event, '${t.id}')"
                 ontouchstart="startDragTable(event, '${t.id}')"
                 ondblclick="editTable('${t.id}')"
                 title="${htmlEscape(titleParts.join(' · '))}">
              <div class="tname">${htmlEscape(t.name)}${isDisabled ? ' 🚫' : ''}</div>
              <div class="tseats">${t.seats}P${ec > 0 ? ' · +' + ec + '🪑' : ''}</div>
            </div>
          `; }).join('')}
        </div>
      </div>
    `;
  }).join('');
}

// =============================================================================
// S199 Raumgestalter — Element-Rendering (SVG) + Interaktion
// =============================================================================
// Ebene UNTER den Tischen: Element-Nodes werden VOR den Tisch-Nodes in den Canvas
// gerendert (DOM-Reihenfolge → Tische malen darüber). KEINE z-index auf Element-
// Nodes (sonst könnten sie über Tische rutschen). Interaktion non-destruktiv:
// Drag bewegt nur das Node, Persistenz + Re-Render erst am Ende.

let elDrag = null;       // Element-Body-Drag (verschieben)
let vtxDrag = null;      // Vertex-Drag (Form ändern)
// S205-HOTFIX — Selektiertes Element (Modul-State, kein DB-Write): NUR daran sind
// die Edit-Affordances sichtbar (⟳-Dreh-Griff + Vertex-Punkte/„+"-Kanten). Klick
// selektiert, leerer Canvas/Escape deselektiert. Überlebt Realtime-Re-Render.
let _selectedEl = null;
let _escBound = false;

/** Element selektieren/deselektieren (id=null). Non-destruktiv: nur der vorher
 *  und der neu selektierte Node werden neu gezeichnet (Griffe ein/aus). */
export function selectElement(id) {
  const prev = _selectedEl;
  const next = id || null;
  if (prev === next) return;
  _selectedEl = next;
  if (prev) { const pe = findEl(prev); if (pe) _rerenderElNode(prev, pe); }
  if (next) { const ne = findEl(next); if (ne) _rerenderElNode(next, ne); }
}

/** Mousedown auf den leeren Canvas (nicht auf ein Element/Tisch) → deselektieren. */
export function lelCanvasMouseDown(e) {
  if (e && e.target === e.currentTarget && _selectedEl) selectElement(null);
}

/** Escape deselektiert — einmalig gebunden (Guard), wirkt nur bei aktiver Selektion. */
function _ensureEscDeselect() {
  if (_escBound || typeof document === 'undefined') return;
  _escBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && _selectedEl) selectElement(null);
  });
}
let _elEdit = null;      // Mini-Edit-Pending { id, rotation, label }

/** Editor-Node: interaktiv (Drag/Doppelklick) + im Ecken-Modus mit Vertex-Handles.
 *  Nutzt `_elementBox` (core/layout-render.js) als Geometrie-Single-Source. */
function renderElementNode(el) {
  const m = KIND_META[el.kind] || {};
  const box = _elementBox(el);
  const editing = _selectedEl === el.id;
  const base = `left:${box.left}px; top:${box.top}px; width:${box.width}px; height:${box.height}px; transform:rotate(${box.rot}deg);`;
  const attrs = `data-el-id="${el.id}"
       onmousedown="startDragElement(event,'${el.id}')" ontouchstart="startDragElement(event,'${el.id}')"
       ondblclick="editElement('${el.id}')" title="${htmlEscape(m.label || el.kind)}"`;
  // S205-HOTFIX — Dreh-Griff (oben am Element, rotiert mit) NUR am selektierten
  // Element (sonst überladen die vielen ⟳ das Bild). Drag = Winkel um die Mitte,
  // 5°-Raster. Für alle Element-Arten (poly + Marker).
  const rotHandle = editing
    ? `<div class="lel-rot-handle" title="Drehen (5°-Raster)"
       onmousedown="startRotateElement(event,'${el.id}')" ontouchstart="startRotateElement(event,'${el.id}')"
       onclick="event.stopPropagation()" ondblclick="event.stopPropagation()">⟳</div>`
    : '';
  if (box.poly) {
    let handles = '';
    if (editing) {
      const bb = box.bb, pad = box.pad, nPts = el.points.length;
      // S205-HF — Kanten-Griffe: eine dünne, ziehbare Leiste ENTLANG jeder Kante
      // (rotiert). Ziehen verschiebt BEIDE Endpunkte zusammen (moveEdge) — keine
      // zwei Einzel-Punkte mehr nötig. Liegt UNTER den Vertex-/„+"-Griffen (z-index),
      // damit die Ecken an den Enden greifbar bleiben.
      const edges = el.points.map((p, i) => {
        if (!el.closed && i === nPts - 1) return '';
        const b = el.points[(i + 1) % nPts];
        const ax = p.x - bb.minX + pad, ay = p.y - bb.minY + pad;
        const bx = b.x - bb.minX + pad, by = b.y - bb.minY + pad;
        const len = Math.hypot(bx - ax, by - ay);
        const ang = Math.atan2(by - ay, bx - ax) * 180 / Math.PI;
        return `<div class="lel-edge" style="left:${ax}px; top:${ay}px; width:${len}px; transform:rotate(${ang}deg);"
           onmousedown="startDragEdge(event,'${el.id}',${i})" ontouchstart="startDragEdge(event,'${el.id}',${i})"
           title="Kante ziehen (beide Punkte)"></div>`;
      }).join('');
      handles = edges + el.points.map((p, i) =>
        `<div class="lel-vtx" style="left:${p.x - bb.minX + pad}px; top:${p.y - bb.minY + pad}px;"
           onmousedown="startDragVertex(event,'${el.id}',${i})" ontouchstart="startDragVertex(event,'${el.id}',${i})"
           ondblclick="removeVertexAt(event,'${el.id}',${i})" title="Ziehen = verschieben · Doppelklick = löschen"></div>`).join('')
        + el.points.map((p, i) => {
            if (!el.closed && i === nPts - 1) return '';
            const b = el.points[(i + 1) % nPts];
            const mx = (p.x + b.x) / 2 - bb.minX + pad, my = (p.y + b.y) / 2 - bb.minY + pad;
            return `<div class="lel-vtx-add" style="left:${mx}px; top:${my}px;"
              onmousedown="addVertexAt(event,'${el.id}',${i})" title="Punkt einfügen">+</div>`;
          }).join('')
        + `<button class="lel-done" onmousedown="event.stopPropagation()" onclick="endVertexEdit()" title="Ecken-Bearbeitung beenden">✓</button>`;
    }
    return `<div class="lel-node lel-poly lel-${el.kind}${editing ? ' editing' : ''}" style="${base}" ${attrs}>${box.inner}${rotHandle}${handles}</div>`;
  }
  return `<div class="lel-node lel-marker lel-${el.kind}" style="${base}" ${attrs}>${box.inner}${rotHandle}</div>`;
}

// ---- Dreh-Griff (S205): Element um seine Mitte rotieren, 5°-Snap ---------------
let elRotate = null;
export function startRotateElement(e, id) {
  e.preventDefault();
  if (e.stopPropagation) e.stopPropagation();   // nicht das ganze Element ziehen
  const el = findEl(id);
  if (!el) return;
  const node = document.querySelector(`#layout-areas [data-el-id="${id}"]`);
  if (!node) return;
  const r = node.getBoundingClientRect();
  elRotate = { id, cx: r.left + r.width / 2, cy: r.top + r.height / 2, rot: clampRotation(el.rotation), moved: false };
  document.addEventListener('mousemove', rotateMove);
  document.addEventListener('mouseup', rotateEnd);
  document.addEventListener('touchmove', rotateMove, { passive: false });
  document.addEventListener('touchend', rotateEnd);
}
function _rotAngleFromPointer(e) {
  const px = (e.touches ? e.touches[0].clientX : e.clientX);
  const py = (e.touches ? e.touches[0].clientY : e.clientY);
  // Griff sitzt oben am Element → „nach oben zeigen" = 0°. +90 verschiebt die
  // atan2-Nullrichtung (rechts) auf oben.
  const deg = Math.atan2(py - elRotate.cy, px - elRotate.cx) * 180 / Math.PI + 90;
  return snapRotation5(deg);
}
function rotateMove(e) {
  if (!elRotate) return;
  e.preventDefault();
  elRotate.rot = _rotAngleFromPointer(e);
  elRotate.moved = true;
  const el = findEl(elRotate.id);
  if (el) _rerenderElNode(elRotate.id, { ...el, rotation: elRotate.rot });
}
async function rotateEnd() {
  if (!elRotate) return;
  document.removeEventListener('mousemove', rotateMove);
  document.removeEventListener('mouseup', rotateEnd);
  document.removeEventListener('touchmove', rotateMove);
  document.removeEventListener('touchend', rotateEnd);
  const cap = elRotate; elRotate = null;
  if (!cap.moved) return;   // bloßer Klick → nichts persistieren
  if (!requireEditPermission('tables', 'Element drehen')) { renderLayoutAreas(); return; }
  try {
    await window.App.storage.layoutElements.update(cap.id, { rotation: cap.rot });
    renderLayoutAreas();
  } catch (e) {
    console.error('[rotateEnd]', e);
    showToast('Drehung nicht gespeichert', 'error');
    renderLayoutAreas();
  }
}

function findEl(id) { return (getState().layoutElements || []).find(x => x.id === id && !x.deletedAt) || null; }
function nextZ(area) {
  const els = elementsFor(area);
  return els.length ? Math.max(...els.map(e => Number(e.zIndex) || 0)) + 1 : 0;
}
function activeLelArea() {
  const sel = document.getElementById('lel-area');
  return (sel && sel.value) || 'indoor';
}
function _rerenderElNode(id, tempEl) {
  const node = document.querySelector(`#layout-areas [data-el-id="${id}"]`);
  if (!node) return;
  const holder = document.createElement('div');
  holder.innerHTML = renderElementNode(tempEl);
  const fresh = holder.firstElementChild;
  if (fresh) node.replaceWith(fresh);
}

// ---- Palette / Schnellaktionen / Canvas ------------------------------------
export async function addElement(kind) {
  if (!requireEditPermission('tables', 'Element hinzufügen')) return;
  const area = activeLelArea();
  const sz = canvasSizeFor(area);
  const el = defaultElement(kind, area, sz.w, sz.h, nextZ(area));
  if (!el) return;
  try {
    await window.App.storage.layoutElements.create(el);
    renderLayoutAreas();
  } catch (e) {
    showToast('Element konnte nicht angelegt werden: ' + (e.message || e), 'error');
    console.error('[addElement]', e);
  }
}

export async function insertOuterWalls() {
  if (!requireEditPermission('tables', 'Außenwände einfügen')) return;
  const area = activeLelArea();
  const sz = canvasSizeFor(area);
  try {
    await window.App.storage.layoutElements.create(outerWallsElement(area, sz.w, sz.h, nextZ(area)));
    renderLayoutAreas();
    showToast('Außenwände eingefügt — Ecken frei anpassbar', 'success');
  } catch (e) {
    showToast('Außenwände fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[insertOuterWalls]', e);
  }
}

export function toggleLelGrid() {
  const on = !gridEnabled();
  setGridEnabled(on);
  document.querySelectorAll('#layout-areas .layout-canvas').forEach(c => c.classList.toggle('lel-grid', on));
  const btn = document.getElementById('lel-grid-btn');
  if (btn) { btn.classList.toggle('active', on); btn.setAttribute('aria-pressed', on ? 'true' : 'false'); }
}

export function onLelAreaChange() {
  const sz = canvasSizeFor(activeLelArea());
  const cw = document.getElementById('lel-cw'), ch = document.getElementById('lel-ch');
  if (cw) cw.value = sz.w;
  if (ch) ch.value = sz.h;
  if (_selectedEl) { _selectedEl = null; renderLayoutAreas(); }
}

export async function applyCanvasSize() {
  if (!requireEditPermission('tables', 'Canvas-Größe ändern')) return;
  const area = activeLelArea();
  const w = Math.max(200, Math.min(4000, parseInt(document.getElementById('lel-cw').value, 10) || 800));
  const h = Math.max(200, Math.min(4000, parseInt(document.getElementById('lel-ch').value, 10) || 500));
  try {
    await window.App.storage.layoutAreas.upsert(area, w, h);
    renderLayoutAreas();
    showToast('Canvas-Größe gespeichert', 'success');
  } catch (e) {
    showToast('Canvas-Größe fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[applyCanvasSize]', e);
  }
}

// ---- Element verschieben (Body-Drag, Snap aufs Gitter, non-destruktiv) ------
export function startDragElement(e, id) {
  const el = findEl(id);
  if (!el) return;
  e.preventDefault();
  const node = e.currentTarget;
  const canvas = node.closest('.layout-canvas');
  if (!canvas) return;
  const startX = (e.touches ? e.touches[0].clientX : e.clientX);
  const startY = (e.touches ? e.touches[0].clientY : e.clientY);
  elDrag = {
    id, node, startX, startY,
    origX: Number(el.x) || 0, origY: Number(el.y) || 0,
    baseLeft: parseFloat(node.style.left) || 0, baseTop: parseFloat(node.style.top) || 0,
    _nx: Number(el.x) || 0, _ny: Number(el.y) || 0, moved: false
  };
  document.addEventListener('mousemove', elDragMove);
  document.addEventListener('mouseup', elDragEnd);
  document.addEventListener('touchmove', elDragMove, { passive: false });
  document.addEventListener('touchend', elDragEnd);
}
function elDragMove(e) {
  if (!elDrag) return;
  e.preventDefault();
  const cx = (e.touches ? e.touches[0].clientX : e.clientX);
  const cy = (e.touches ? e.touches[0].clientY : e.clientY);
  const dx = cx - elDrag.startX, dy = cy - elDrag.startY;
  const nx = Math.max(0, snap10(elDrag.origX + dx));
  const ny = Math.max(0, snap10(elDrag.origY + dy));
  elDrag._nx = nx; elDrag._ny = ny;
  elDrag.node.style.left = (elDrag.baseLeft + (nx - elDrag.origX)) + 'px';
  elDrag.node.style.top  = (elDrag.baseTop  + (ny - elDrag.origY)) + 'px';
  if (Math.abs(dx) > 3 || Math.abs(dy) > 3) elDrag.moved = true;
}
async function elDragEnd() {
  if (!elDrag) return;
  document.removeEventListener('mousemove', elDragMove);
  document.removeEventListener('mouseup', elDragEnd);
  document.removeEventListener('touchmove', elDragMove);
  document.removeEventListener('touchend', elDragEnd);
  const cap = elDrag; elDrag = null;
  // S205-HOTFIX — mousedown+up OHNE Bewegung = Klick → Element selektieren (Griffe
  // erscheinen). Kein Zwei-Klick-Zwang: bei Bewegung war es ein Drag (unten).
  if (!cap.moved) { selectElement(cap.id); return; }
  if (!requireEditPermission('tables', 'Element verschieben')) { renderLayoutAreas(); return; }
  try {
    await window.App.storage.layoutElements.update(cap.id, { x: cap._nx, y: cap._ny });
    // Nach dem Verschieben bleibt das Element selektiert (Griffe am neuen Ort).
    _selectedEl = cap.id;
    renderLayoutAreas();
  } catch (e) {
    console.error('[elDragEnd]', e);
    showToast('Position nicht gespeichert (Backend-Fehler)', 'error');
    renderLayoutAreas();
  }
}

// ---- Vertex-Edit (Form ändern; Pointer-Delta inverse-rotiert in Lokalkoords) -
function rotVec(dx, dy, deg) {
  const r = deg * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
  return { x: dx * c - dy * s, y: dx * s + dy * c };
}
export function startDragVertex(e, id, idx) {
  e.preventDefault();
  if (e.stopPropagation) e.stopPropagation();   // nicht das ganze Element ziehen
  const el = findEl(id);
  if (!el || !Array.isArray(el.points)) return;
  vtxDrag = {
    // S205 — Kern-Regression: der Pointer-Delta wird mit dem EXAKTEN Winkel
    // inverse-rotiert (clampRotation, kein 45°-Snap), sonst driftet die Ecke bei
    // krummen Winkeln (z. B. 32°).
    id, idx, rot: clampRotation(el.rotation),
    points: el.points.map(p => ({ x: p.x, y: p.y })),
    startX: (e.touches ? e.touches[0].clientX : e.clientX),
    startY: (e.touches ? e.touches[0].clientY : e.clientY),
    _np: { x: el.points[idx].x, y: el.points[idx].y }, moved: false
  };
  document.addEventListener('mousemove', vtxDragMove);
  document.addEventListener('mouseup', vtxDragEnd);
  document.addEventListener('touchmove', vtxDragMove, { passive: false });
  document.addEventListener('touchend', vtxDragEnd);
}
function vtxDragMove(e) {
  if (!vtxDrag) return;
  e.preventDefault();
  const cx = (e.touches ? e.touches[0].clientX : e.clientX);
  const cy = (e.touches ? e.touches[0].clientY : e.clientY);
  const loc = rotVec(cx - vtxDrag.startX, cy - vtxDrag.startY, -vtxDrag.rot);
  const p0 = vtxDrag.points[vtxDrag.idx];
  vtxDrag._np = snapPoint({ x: p0.x + loc.x, y: p0.y + loc.y });
  if (Math.abs(cx - vtxDrag.startX) > 2 || Math.abs(cy - vtxDrag.startY) > 2) vtxDrag.moved = true;
  const el = findEl(vtxDrag.id);
  if (el) _rerenderElNode(vtxDrag.id, { ...el, points: moveVertex(vtxDrag.points, vtxDrag.idx, vtxDrag._np) });
}
async function vtxDragEnd() {
  if (!vtxDrag) return;
  document.removeEventListener('mousemove', vtxDragMove);
  document.removeEventListener('mouseup', vtxDragEnd);
  document.removeEventListener('touchmove', vtxDragMove);
  document.removeEventListener('touchend', vtxDragEnd);
  const cap = vtxDrag; vtxDrag = null;
  if (!cap.moved) return;   // bloßer Klick (kein Move) — Node NICHT neu bauen (sonst
                            // stirbt es vor dem Doppelklick-zum-Löschen). Kein Persist.
  if (!requireEditPermission('tables', 'Ecke verschieben')) { renderLayoutAreas(); return; }
  try {
    await window.App.storage.layoutElements.update(cap.id, { points: moveVertex(cap.points, cap.idx, cap._np) });
    renderLayoutAreas();
  } catch (e) {
    console.error('[vtxDragEnd]', e);
    showToast('Form nicht gespeichert', 'error');
    renderLayoutAreas();
  }
}

// ---- Edge-Drag (S205-HF): GANZE Kante ziehen (beide Endpunkte, inverse-rotiert) --
let edgeDrag = null;
export function startDragEdge(e, id, i) {
  e.preventDefault();
  if (e.stopPropagation) e.stopPropagation();   // nicht das ganze Element ziehen
  const el = findEl(id);
  if (!el || !Array.isArray(el.points)) return;
  edgeDrag = {
    id, i, rot: clampRotation(el.rotation),
    points: el.points.map(p => ({ x: p.x, y: p.y })),
    startX: (e.touches ? e.touches[0].clientX : e.clientX),
    startY: (e.touches ? e.touches[0].clientY : e.clientY),
    _pts: el.points.map(p => ({ x: p.x, y: p.y })), moved: false
  };
  document.addEventListener('mousemove', edgeDragMove);
  document.addEventListener('mouseup', edgeDragEnd);
  document.addEventListener('touchmove', edgeDragMove, { passive: false });
  document.addEventListener('touchend', edgeDragEnd);
}
function edgeDragMove(e) {
  if (!edgeDrag) return;
  e.preventDefault();
  const cx = (e.touches ? e.touches[0].clientX : e.clientX);
  const cy = (e.touches ? e.touches[0].clientY : e.clientY);
  const loc = rotVec(cx - edgeDrag.startX, cy - edgeDrag.startY, -edgeDrag.rot);
  edgeDrag._pts = moveEdge(edgeDrag.points, edgeDrag.i, loc);
  if (Math.abs(cx - edgeDrag.startX) > 2 || Math.abs(cy - edgeDrag.startY) > 2) edgeDrag.moved = true;
  const el = findEl(edgeDrag.id);
  if (el) _rerenderElNode(edgeDrag.id, { ...el, points: edgeDrag._pts });
}
async function edgeDragEnd() {
  if (!edgeDrag) return;
  document.removeEventListener('mousemove', edgeDragMove);
  document.removeEventListener('mouseup', edgeDragEnd);
  document.removeEventListener('touchmove', edgeDragMove);
  document.removeEventListener('touchend', edgeDragEnd);
  const cap = edgeDrag; edgeDrag = null;
  if (!cap.moved) return;
  if (!requireEditPermission('tables', 'Kante verschieben')) { renderLayoutAreas(); return; }
  try {
    await window.App.storage.layoutElements.update(cap.id, { points: cap._pts });
    renderLayoutAreas();
  } catch (e) {
    console.error('[edgeDragEnd]', e);
    showToast('Form nicht gespeichert', 'error');
    renderLayoutAreas();
  }
}
export async function addVertexAt(e, id, edgeIdx) {
  if (e) { e.preventDefault(); if (e.stopPropagation) e.stopPropagation(); }
  if (!requireEditPermission('tables', 'Punkt einfügen')) return;
  const el = findEl(id);
  if (!el || !Array.isArray(el.points)) return;
  try {
    await window.App.storage.layoutElements.update(id, { points: addVertex(el.points, edgeIdx) });
    renderLayoutAreas();
  } catch (err) { showToast('Punkt einfügen fehlgeschlagen', 'error'); console.error('[addVertexAt]', err); }
}
export async function removeVertexAt(e, id, idx) {
  if (e && e.stopPropagation) e.stopPropagation();   // nicht zum Node bubblen (Mini-Edit)
  if (!requireEditPermission('tables', 'Punkt löschen')) return;
  const el = findEl(id);
  if (!el || !Array.isArray(el.points)) return;
  const min = (KIND_META[el.kind] && !KIND_META[el.kind].line) ? 3 : 2;
  if (el.points.length <= min) { showToast('Mindestanzahl Punkte erreicht', 'info'); return; }
  try {
    await window.App.storage.layoutElements.update(id, { points: removeVertex(el.points, idx, min) });
    renderLayoutAreas();
  } catch (err) { showToast('Punkt löschen fehlgeschlagen', 'error'); console.error('[removeVertexAt]', err); }
}
export function beginVertexEdit(id) {
  closeModal();
  selectElement(id);   // Selektion zeigt Vertex-Punkte + ⟳ (non-destruktiv)
  showToast('Ecken bearbeiten: Punkt ziehen · „+" einfügen · Doppelklick löschen · ✓ beendet', 'info');
}
export function endVertexEdit() {
  selectElement(null);
}

// ---- Mini-Edit (Drehung frei / Text / Löschen) — styled, kein prompt() -------
export function editElement(id) {
  const el = findEl(id);
  if (!el) return;
  _elEdit = { id, rotation: clampRotation(el.rotation), label: el.label || '' };
  _renderElEditModal(el);
}
function _renderElEditModal(el) {
  const m = KIND_META[el.kind] || {};
  const labelField = el.kind === 'label'
    ? `<div class="field"><label>Text</label><input id="lel-el-label" type="text" value="${htmlEscape(_elEdit.label)}"></div>` : '';
  const shapeBtn = m.poly
    ? `<div class="field"><button class="btn btn-ghost btn-sm" onclick="beginVertexEdit('${el.id}')">🔺 Ecken bearbeiten</button>
         <div class="hint">Punkte ziehen für Schrägen/Diagonalen; „+" fügt einen Punkt ein.</div></div>` : '';
  document.getElementById('modal-content').innerHTML = `
    <div class="modal-header">
      <h2>${m.emoji || ''} ${htmlEscape(m.label || el.kind)}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field">
      <label for="lel-el-rot">Drehung (Grad, 0–359)</label>
      <div class="lel-rot-group">
        <input id="lel-el-rot" type="number" min="0" max="359" step="5" value="${_elEdit.rotation}"
               oninput="setElementRot('${el.id}', this.value)" style="width:90px;">
        <span class="hint">frei drehbar; der Griff am Element rastet in 5°-Schritten.</span>
      </div>
    </div>
    ${labelField}
    ${shapeBtn}
    <div class="btn-row">
      <button class="btn btn-primary" onclick="saveElementEdit('${el.id}')">Speichern</button>
      <button class="btn btn-danger" onclick="deleteElement('${el.id}')">Löschen</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
  document.getElementById('modal-overlay').classList.add('active');
}
export function setElementRot(id, deg) {
  if (!_elEdit || _elEdit.id !== id) return;
  const lab = document.getElementById('lel-el-label');
  if (lab) _elEdit.label = lab.value;
  // Freies Gradfeld: exakter Wert (clamp 0–359), KEIN Modal-Re-Render (sonst
  // verliert das Eingabefeld den Fokus) — nur Live-Preview des Nodes.
  _elEdit.rotation = clampRotation(deg);
  const el = findEl(id);
  if (el) _rerenderElNode(id, { ...el, rotation: _elEdit.rotation });
}
export async function saveElementEdit(id) {
  if (!requireEditPermission('tables', 'Element ändern')) return;
  const el = findEl(id);
  if (!el || !_elEdit) return;
  const lab = document.getElementById('lel-el-label');
  if (lab) _elEdit.label = lab.value;
  const changes = { rotation: clampRotation(_elEdit.rotation) };
  if (el.kind === 'label') changes.label = (_elEdit.label || 'Text').trim() || 'Text';
  try {
    await window.App.storage.layoutElements.update(id, changes);
    closeModal();
    renderLayoutAreas();
    showToast('Element aktualisiert', 'success');
  } catch (e) {
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[saveElementEdit]', e);
  }
}
export async function deleteElement(id) {
  if (!requireEditPermission('tables', 'Element löschen')) return;
  const _d = await window.styledConfirm({
    title: 'Element löschen?', danger: true, confirmLabel: 'Löschen',
    body: 'Das Struktur-Element wird aus dem Plan entfernt.'
  });
  if (!_d.confirmed) return;
  try {
    await window.App.storage.layoutElements.softDelete(id, _d.reason || '');
    closeModal();
    if (_selectedEl === id) _selectedEl = null;
    renderLayoutAreas();
    showToast('Element gelöscht', 'success');
  } catch (e) {
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[deleteElement]', e);
  }
}

// -----------------------------------------------------------------------------
// Drag & Drop (Tische — UNVERÄNDERT, snap-frei, Null-Regression S199)
// -----------------------------------------------------------------------------
let dragState = null;

export function startDragTable(e, id) {
  const state = getState();
  e.preventDefault();
  const table = state.tables.find(t => t.id === id);
  if (!table) return;
  const node = e.currentTarget;
  const canvas = node.parentElement;
  const canvasRect = canvas.getBoundingClientRect();
  const startX = (e.touches ? e.touches[0].clientX : e.clientX);
  const startY = (e.touches ? e.touches[0].clientY : e.clientY);
  dragState = {
    id, node, canvas, canvasRect,
    startX, startY,
    origX: table.x, origY: table.y,
    moved: false
  };
  document.addEventListener('mousemove', dragMove);
  document.addEventListener('mouseup', dragEnd);
  document.addEventListener('touchmove', dragMove, { passive: false });
  document.addEventListener('touchend', dragEnd);
}

function dragMove(e) {
  if (!dragState) return;
  e.preventDefault();
  const cx = (e.touches ? e.touches[0].clientX : e.clientX);
  const cy = (e.touches ? e.touches[0].clientY : e.clientY);
  let nx = dragState.origX + (cx - dragState.startX);
  let ny = dragState.origY + (cy - dragState.startY);
  nx = Math.max(0, Math.min(dragState.canvasRect.width  - dragState.node.offsetWidth,  nx));
  ny = Math.max(0, Math.min(dragState.canvasRect.height - dragState.node.offsetHeight, ny));
  dragState.node.style.left = nx + 'px';
  dragState.node.style.top  = ny + 'px';
  dragState._lastX = nx; dragState._lastY = ny;
  if (Math.abs(cx - dragState.startX) > 3 || Math.abs(cy - dragState.startY) > 3) {
    dragState.moved = true;
  }
}

async function dragEnd() {
  if (!dragState) return;
  document.removeEventListener('mousemove', dragMove);
  document.removeEventListener('mouseup', dragEnd);
  document.removeEventListener('touchmove', dragMove);
  document.removeEventListener('touchend', dragEnd);
  const captured = dragState;
  dragState = null;
  if (captured.moved) {
    // Phase 41 (S51): staff/host können Tische zwar visuell ziehen
    // (Drag ist nur DOM-Style), die Persistenz wird aber von RLS geblockt.
    // Mit dem Gate gibt's einen klaren Toast statt eines stillen Backend-
    // Fehlers, und der nächste Re-Render setzt die Position zurück.
    if (!requireEditPermission('tables', 'Tisch verschieben')) return;
    const state = getState();
    const t = state.tables.find(x => x.id === captured.id);
    if (!t) return;
    const newX = Math.round(captured._lastX != null ? captured._lastX : t.x);
    const newY = Math.round(captured._lastY != null ? captured._lastY : t.y);
    try {
      await window.App.storage.tables.update(t.id, { x: newX, y: newY });
    } catch (e) {
      console.error('[dragEnd] Position konnte nicht gespeichert werden:', e);
      showToast('Position nicht gespeichert (Backend-Fehler)', 'error');
    }
  }
}

// -----------------------------------------------------------------------------
// Tisch-CRUD
// -----------------------------------------------------------------------------
export async function addTable() {
  // Phase 41 (S51): Defense-in-Depth — tables.INSERT ist via RLS auf
  // owner/admin/manager beschränkt (Migration phase_41). Client-Gate
  // verhindert Roundtrip + gibt User klaren Toast.
  if (!requireEditPermission('tables', 'Tisch anlegen')) return;
  const state = getState();
  const area       = document.getElementById('new-table-area').value;
  const seats      = parseInt(document.getElementById('new-table-seats').value, 10);
  const extendable = document.getElementById('new-table-extendable').checked;
  const extraChairs = Math.max(0, Math.min(6, parseInt(document.getElementById('new-table-extra-chairs').value, 10) || 0));
  const prefix = area === 'indoor' ? 'I' : area === 'outdoor' ? 'A' : 'C';
  const sameArea = state.tables.filter(t => t.area === area);
  const num = sameArea.length + 1;
  try {
    await window.App.storage.tables.create({
      id: uid('t'),
      name: prefix + num,
      area, seats, extendable, extraChairs,
      shape: 'rectangular',
      disabled: false,
      x: Math.round(20 + Math.random() * 100),
      y: Math.round(20 + Math.random() * 100)
    });
    renderLayoutAreas();
    showToast('Tisch hinzugefügt', 'success');
  } catch (e) {
    showToast('Tisch konnte nicht angelegt werden: ' + (e.message || e), 'error');
    console.error('[addTable]', e);
  }
}

export function editTable(id) {
  const state = getState();
  const t = state.tables.find(x => x.id === id);
  if (!t) return;
  const html = `
    <div class="modal-header">
      <h2>Tisch bearbeiten</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <div class="field">
      <label>Name</label>
      <input id="et-name" type="text" value="${htmlEscape(t.name)}">
    </div>
    <div class="field">
      <label>Sitzplätze</label>
      <input id="et-seats" type="number" min="1" max="20" value="${t.seats}">
    </div>
    <div class="field">
      <label>Bereich</label>
      <select id="et-area">
        <option value="indoor"  ${t.area==='indoor'?'selected':''}>Innen</option>
        <option value="outdoor" ${t.area==='outdoor'?'selected':''}>Außen</option>
        <option value="club"    ${t.area==='club'?'selected':''}>Club</option>
      </select>
    </div>
    <div class="field">
      <label>Form</label>
      <select id="et-shape">
        <option value="rectangular" ${(t.shape || 'rectangular')==='rectangular'?'selected':''}>Eckig</option>
        <option value="round"       ${t.shape==='round'?'selected':''}>Rund</option>
      </select>
      <div class="hint">Nur visuell — beeinflusst Kombi-/Zuweisungslogik nicht.</div>
    </div>
    <div class="field">
      <label class="checkbox">
        <input type="checkbox" id="et-extendable" ${t.extendable !== false ? 'checked' : ''}>
        Kombinierbar (kann mit Nachbartischen zusammengeschoben werden)
      </label>
      <div class="hint">Deaktivieren für feste Bistro-Tische, die nicht bewegt werden können.</div>
    </div>
    <div class="field">
      <label>Zusatzstühle</label>
      <input id="et-extra-chairs" type="number" min="0" max="6" step="1" value="${tableExtraChairs(t)}">
      <div class="hint">Wie viele zusätzliche Stühle dürfen an diesen Tisch gestellt werden?</div>
    </div>
    <div class="field">
      <label class="checkbox">
        <input type="checkbox" id="et-disabled" ${t.disabled === true ? 'checked' : ''}>
        Tisch außer Betrieb (dauerhaft sperren)
      </label>
      <div class="hint">Nutze diese Option z.B. für defekte oder dauerhaft intern reservierte Tische.</div>
    </div>
    <div class="btn-row">
      <button class="btn btn-primary" onclick="saveTableEdit('${t.id}')">Speichern</button>
      <button class="btn btn-danger" onclick="deleteTable('${t.id}')">Löschen</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export async function saveTableEdit(id) {
  if (!requireEditPermission('tables', 'Tisch ändern')) return;
  const state = getState();
  const t = state.tables.find(x => x.id === id);
  if (!t) return;
  const changes = {
    name:        document.getElementById('et-name').value.trim() || t.name,
    seats:       parseInt(document.getElementById('et-seats').value, 10) || t.seats,
    area:        document.getElementById('et-area').value,
    shape:       document.getElementById('et-shape').value,
    extendable:  document.getElementById('et-extendable').checked,
    extraChairs: Math.max(0, Math.min(6, parseInt(document.getElementById('et-extra-chairs').value, 10) || 0)),
    disabled:    document.getElementById('et-disabled').checked
  };
  try {
    await window.App.storage.tables.update(id, changes);
    closeModal();
    renderLayoutAreas();
    showToast('Tisch aktualisiert', 'success');
  } catch (e) {
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[saveTableEdit]', e);
  }
}

export async function deleteTable(id) {
  if (!requireEditPermission('tables', 'Tisch löschen')) return;
  // S58 Block II (Phase 45): Soft-Delete + Audit-Trail. Tische sind in
  // capacity_check + Reservierung-Zuordnung verlinkt → Hard-Delete wäre
  // riskant. Soft-Delete erlaubt Restore + dokumentiert wer/wann/warum.
  const _del = await window.styledConfirm({
    title: 'Diesen Tisch löschen?', danger: true, confirmLabel: 'Löschen',
    body: 'Kein Hard-Delete — der Tisch bleibt wiederherstellbar (Bereich „🗑️ Gelöschte Tische" unten).',
    reasonField: true
  });
  if (!_del.confirmed) return;
  const reason = _del.reason || '';
  try {
    await window.App.storage.tables.softDelete(id, reason);
    closeModal();
    renderLayoutAreas();
    await renderDeletedTablesList();
    showToast('Tisch gelöscht (wiederherstellbar)', 'success');
  } catch (e) {
    showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[deleteTable]', e);
  }
}

/**
 * S58 Hotfix-2 — Restore-View für soft-gelöschte Tische.
 * Analog renderDeletedMenuList() in menu-editor.js — Konsistenz-Regel
 * (Memory `feedback_gg_hard_way`). Lädt deleted_at IS NOT NULL aus der DB
 * (nicht im aktiven _state.tables) und rendert sie als kollabierbare Liste
 * am Ende des Layout-Editors.
 */
export async function renderDeletedTablesList() {
  const wrap = document.getElementById('tables-deleted-list');
  if (!wrap) return;
  let items = [];
  try {
    items = await window.App.storage.tables.listDeleted();
  } catch (e) {
    console.warn('[layout] tables.listDeleted:', e);
  }
  const AREA_LABELS = { indoor: 'Innen', outdoor: 'Außen', club: 'Club' };
  if (!items.length) {
    wrap.innerHTML = `
      <div class="hint" style="padding:12px; text-align:center;">
        Keine gelöschten Tische. (Hier landen Tische nach „Löschen" im Edit-Modal.)
      </div>`;
    return;
  }
  wrap.innerHTML = `
    <div style="font-size:.85rem; color:var(--ink-soft); margin-bottom:10px;">
      ${items.length} Eintrag/Einträge — Wiederherstellung möglich, solange der
      Tisch nicht final aus der DB entfernt wurde.
    </div>
    <div style="display:flex; flex-direction:column; gap:8px;">
    ${items.map(t => `
      <div data-deleted-table="${htmlEscape(t.id)}"
           style="display:grid; grid-template-columns:1fr auto; gap:12px; align-items:center;
                  padding:10px 14px; background:var(--bg-soft, #f6f6f6);
                  border:1px solid var(--border, #e2e2e2); border-radius:8px;">
        <div style="min-width:0;">
          <div style="font-weight:600; line-height:1.2;">${htmlEscape(t.name || '—')}</div>
          <div style="color:var(--ink-soft); font-size:.82rem; margin-top:2px;">
            ${htmlEscape(AREA_LABELS[t.area] || t.area || '—')} · ${t.seats || 0} Plätze
            · gelöscht ${t.deletedAt ? new Date(t.deletedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
          </div>
          ${t.deletedReason ? `
            <div style="color:var(--ink-soft); font-size:.78rem; margin-top:2px; font-style:italic;">
              Grund: ${htmlEscape(t.deletedReason)}
            </div>` : ''}
        </div>
        <button class="btn btn-primary btn-sm" style="white-space:nowrap;"
                onclick="restoreTable('${t.id}')">
          ↺ Wiederherstellen
        </button>
      </div>
    `).join('')}
    </div>
  `;
}

export async function restoreTable(id) {
  if (!requireEditPermission('tables', 'Tisch wiederherstellen')) return;
  try {
    await window.App.storage.tables.restore(id);
    renderLayoutAreas();
    await renderDeletedTablesList();
    showToast('Tisch wiederhergestellt', 'success');
  } catch (e) {
    showToast('Wiederherstellen fehlgeschlagen: ' + (e.message || e), 'error');
    console.error('[restoreTable]', e);
  }
}

export async function resetTablesToDefault() {
  if (!requireEditPermission('tables', 'Tisch-Layout zurücksetzen')) return;
  if (!(await window.styledConfirm({ title: 'Tischanordnung auf Standard zurücksetzen?', body: 'Reservierungen bleiben erhalten.', danger: true, confirmLabel: 'Zurücksetzen' })).confirmed) return;
  showToast('Setze Tische zurück...', '');
  try {
    const existing = await window.App.storage.tables.list();
    for (const t of existing) {
      await window.App.storage.tables.remove(t.id);
    }
    // defaultState() lebt noch in app.js → via window aufrufen
    const defaults = (typeof window.defaultState === 'function') ? window.defaultState().tables : [];
    for (const t of defaults) {
      await window.App.storage.tables.create(t);
    }
    renderLayoutAreas();
    showToast('Tische zurückgesetzt', 'success');
  } catch (e) {
    console.error('[resetTablesToDefault]', e);
    showToast('Zurücksetzen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

/* =============================================================================
   S243 Cut A — QR-Aufsteller drucken
   =============================================================================
   Eine Karte je Tisch: Logo, Tischname, QR, „Scannen & bestellen" und die URL
   im Klartext (der QR-Scanner am Handy versagt manchmal, ein abgetipptes
   Kürzel nie — dafür lässt das Token-Alphabet `l`, `o`, `0` und `1` weg).

   Die Route `#tisch/<token>` selbst kommt erst in Cut B. Der QR wird aber JETZT
   gedruckt und aufgestellt, also muss das URL-Format ab hier stehen — deshalb
   liegt es als `tableQrUrl()` in core/table-qr.js und nicht in der Cut-B-Route.

   Druck-Mechanik wie beim Bon (receipt.js `printReceiptHtml`): Sheet als
   direktes body-Kind, Body-Klassen-Marker, Aufräumen im `afterprint`. Kein
   iframe (browser-übergreifend zickig), kein <style> im innerHTML — die Regeln
   stehen in styles.css (Memory feedback_gg_style_tag_global_leak).
============================================================================ */

const QR_SHEET_ID = 'table-qr-print';
const QR_PAGE_STYLE_ID = 'gg-table-qr-page-size';
let _qrSheetKeyHandler = null;

/** Gast-Origin für die gedruckte URL — aus `AppConfig.guestOrigin`, NICHT aus
 *  `location.origin` (S244-HF: aus dem Dev-Cockpit gedruckte Bögen trugen
 *  `http://127.0.0.1:5500/…` und waren am Handy tot). Auflösung liegt pure in
 *  core/table-qr.js. */
function guestOrigin() {
  const cfg = (typeof window !== 'undefined' && window.AppConfig) || null;
  const loc = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
  return resolveGuestOrigin(cfg, loc);
}

/**
 * @page auf A4 stellen — und zwar NUR solange die Aufsteller gedruckt werden.
 *
 * receipt.js injiziert ein `<style media="print">` mit `@page { size: 80mm auto }`.
 * `@page` lässt sich nicht über eine Body-Klasse scopen, deshalb gewinnt hier
 * schlicht das später im <head> stehende Tag. Genau darum wird dieses Tag im
 * `afterprint` wieder ENTFERNT: bliebe es stehen, käme der nächste Kassenbon
 * auf A4 statt auf die 80-mm-Rolle.
 */
function applyQrPageSize(on) {
  const existing = document.getElementById(QR_PAGE_STYLE_ID);
  if (!on) {
    if (existing) existing.remove();
    return;
  }
  const tag = existing || document.createElement('style');
  tag.id = QR_PAGE_STYLE_ID;
  tag.setAttribute('media', 'print');
  tag.textContent = '@page { size: A4 portrait; margin: 10mm; }';
  if (!existing) document.head.appendChild(tag);
}

/** Eine Karte. `qrSvg` ist bereits gerendertes SVG-Markup. */
function _qrCardHtml(table, url, qrSvg, logoSrc, brand) {
  return `
    <article class="qr-card">
      <img class="qr-card-logo" src="${htmlEscape(logoSrc)}" alt="">
      <div class="qr-card-brand">${htmlEscape(brand)}</div>
      <div class="qr-card-table">${htmlEscape(table.name || table.id)}</div>
      <div class="qr-card-qr">${qrSvg}</div>
      <div class="qr-card-cta">Scannen &amp; bestellen</div>
      <div class="qr-card-url">${htmlEscape(url)}</div>
      <div class="qr-card-actions no-print">
        <button class="btn btn-ghost btn-sm" onclick="rotateTableQr('${htmlEscape(table.id)}')"
                title="Neues Token erzeugen — der gedruckte Code wird ungültig">♻️ QR neu erzeugen</button>
        ${table.qrTokenRotatedAt
          ? `<span class="qr-card-rotated">erneuert ${htmlEscape(new Date(table.qrTokenRotatedAt).toLocaleDateString('de-DE'))}</span>`
          : ''}
      </div>
    </article>`;
}

const AREA_LABELS = { indoor: 'Innen', outdoor: 'Außen', club: 'Club' };

/**
 * Druckansicht öffnen. Lädt die Tokens separat (sie liegen bewusst NICHT im
 * Boot-Cache — `anon` darf die Spalte nicht sehen, s. phase_176 / storage.js
 * TABLES_BOOT_COLUMNS) und rendert die QR-Codes asynchron.
 */
export async function openTableQrPrint() {
  const adapter = window.App && window.App.storage && window.App.storage.tables;
  if (!adapter || typeof adapter.listQrTokens !== 'function') {
    showToast('QR-Druck ist in dieser Version nicht verfügbar.', 'error');
    return;
  }

  closeTableQrPrint();                         // doppeltes Öffnen → ein Sheet
  const sheet = document.createElement('div');
  sheet.id = QR_SHEET_ID;
  sheet.className = 'qr-sheet menu-paper-scope';
  sheet.innerHTML = `
    <div class="qr-sheet-bar no-print">
      <strong>QR-Aufsteller</strong>
      <span class="qr-sheet-hint">wird geladen …</span>
      <button class="btn btn-primary btn-sm" onclick="printTableQrSheet()">🖨️ Drucken</button>
      <button class="btn btn-ghost btn-sm" onclick="closeTableQrPrint()">Schließen</button>
    </div>
    <div class="qr-sheet-body"><p class="qr-sheet-loading">QR-Codes werden erzeugt …</p></div>`;
  document.body.appendChild(sheet);

  // ESC + Klick auf den Rand schließen — wie bei den anderen Overlays.
  _qrSheetKeyHandler = (e) => { if (e.key === 'Escape') closeTableQrPrint(); };
  document.addEventListener('keydown', _qrSheetKeyHandler);

  let tables;
  try {
    tables = await adapter.listQrTokens();
  } catch (err) {
    console.error('[openTableQrPrint]', err);
    const body = sheet.querySelector('.qr-sheet-body');
    if (body) {
      body.innerHTML = `<p class="qr-sheet-loading">QR-Codes konnten nicht geladen werden: ${htmlEscape(err.message || String(err))}</p>`;
    }
    return;
  }

  const active = (tables || []).filter(t => t.disabled !== true);
  const skipped = (tables || []).length - active.length;
  active.sort((a, b) =>
    String(a.area || '').localeCompare(String(b.area || ''))
    || String(a.name || '').localeCompare(String(b.name || ''), 'de', { numeric: true }));

  const state = getState();
  const brand = (state.settings && state.settings.restaurantName) || 'Grill & Green';
  const logoSrc = getBrandLogoSrc();
  const origin = guestOrigin();

  const cards = [];
  for (const t of active) {
    const url = tableQrUrl(origin, t.qrToken);
    // errorLevel 'Q': der Aufsteller steht auf einem Esstisch — Fingerabdrücke,
    // Soßenspritzer und Knicke sind der Normalfall, nicht die Ausnahme.
    const svg = await generateQrSvg(url, {
      cellSize: 4, margin: 2, errorLevel: 'Q', ariaLabel: `QR-Code Tisch ${t.name || t.id}`
    });
    cards.push(_qrCardHtml(t, url, svg, logoSrc, brand));
  }

  // Zwischenzeitlich geschlossen? Dann nichts mehr anfassen.
  const live = document.getElementById(QR_SHEET_ID);
  if (!live) return;
  const hint = live.querySelector('.qr-sheet-hint');
  if (hint) {
    hint.textContent = `${active.length} ${active.length === 1 ? 'Tisch' : 'Tische'}`
      + (skipped > 0 ? ` · ${skipped} außer Betrieb übersprungen` : '');
  }
  const body = live.querySelector('.qr-sheet-body');
  if (body) {
    body.innerHTML = cards.length
      ? cards.join('')
      : '<p class="qr-sheet-loading">Keine Tische im Plan — leg zuerst welche an.</p>';
  }
  // Bereichs-Info in der Leiste ergänzen (nur Kosmetik, hilft beim Verteilen).
  const areas = Array.from(new Set(active.map(t => AREA_LABELS[t.area] || t.area).filter(Boolean)));
  if (hint && areas.length) hint.textContent += ` · ${areas.join(', ')}`;
}

/** Druckansicht schließen + Listener abmelden (HF-13-Lehre: ein Overlay ohne
 *  sauberes Schließen sperrt das Cockpit bis zum Reload). */
export function closeTableQrPrint() {
  const sheet = document.getElementById(QR_SHEET_ID);
  if (sheet) sheet.remove();
  if (_qrSheetKeyHandler) {
    document.removeEventListener('keydown', _qrSheetKeyHandler);
    _qrSheetKeyHandler = null;
  }
  document.body.classList.remove('printing-table-qr');
  applyQrPageSize(false);
}

/** Drucken. Body-Marker + A4-@page nur für die Dauer des Drucks. */
export function printTableQrSheet() {
  if (!document.getElementById(QR_SHEET_ID)) return;
  applyQrPageSize(true);
  document.body.classList.add('printing-table-qr');
  const cleanup = () => {
    document.body.classList.remove('printing-table-qr');
    applyQrPageSize(false);
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
}

/**
 * S243 Cut A — Token eines Tisches neu erzeugen. Der gedruckte Aufsteller wird
 * damit unbrauchbar, deshalb styledConfirm mit genau dieser Ansage.
 */
export async function rotateTableQr(tableId) {
  if (!requireEditPermission('tables', 'QR-Code neu erzeugen')) return;
  const t = (getState().tables || []).find(x => x.id === tableId);
  const name = (t && t.name) || tableId;
  const ok = await window.styledConfirm({
    title: `QR-Code für ${name} neu erzeugen?`,
    body: 'Der bereits gedruckte Aufsteller an diesem Tisch wird damit ungültig — '
        + 'ein Scan des alten Codes führt danach ins Leere. Nur machen, wenn der '
        + 'Aufsteller danach ersetzt wird.',
    confirmLabel: 'Neu erzeugen',
    danger: true
  });
  if (!ok.confirmed) return;
  try {
    await window.App.storage.tables.rotateQrToken(tableId);
    showToast(`Neuer QR-Code für ${name} — bitte neu ausdrucken.`, 'success');
    // Sheet neu aufbauen, damit die Karte den neuen Code zeigt.
    if (document.getElementById(QR_SHEET_ID)) await openTableQrPrint();
  } catch (e) {
    console.error('[rotateTableQr]', e);
    showToast('Neues Token fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderLayout, renderLayoutAreas,
    startDragTable,
    // S243 Cut A — QR-Aufsteller
    openTableQrPrint, closeTableQrPrint, printTableQrSheet, rotateTableQr,
    addTable, editTable, saveTableEdit, deleteTable, resetTablesToDefault,
    // S58 Hotfix-2
    restoreTable, renderDeletedTablesList,
    // S199 Raumgestalter — Element-Handler (inline-onclick über dom-bindings)
    addElement, insertOuterWalls, toggleLelGrid, onLelAreaChange, applyCanvasSize,
    startDragElement, startDragVertex, startDragEdge, addVertexAt, removeVertexAt,
    beginVertexEdit, endVertexEdit,
    editElement, setElementRot, saveElementEdit, deleteElement,
    // S205 — Dreh-Griff + Selektion (Griffe nur am selektierten Element)
    startRotateElement, selectElement, lelCanvasMouseDown
  });
}
